/**
 * Message Feedback Page
 * Dashboard for viewing user like/dislike feedback on AI messages
 */

import { useState, useEffect, useCallback } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Filter,
  MessageSquare,
} from "lucide-react";

// Types
interface MessageFeedbackItem {
  id: string;
  user_id: string;
  topic_id: string;
  run_id: string;
  feedback_type: "like" | "dislike";
  feedback_note: string | null;
  user_message: string | null;
  assistant_message: string | null;
  agent_type: string | null;
  model_id: string | null;
  context_data: Record<string, unknown> | null;
  device_info: Record<string, unknown> | null;
  created_at: string;
}

interface FeedbackStats {
  overall: {
    like: number;
    dislike: number;
    total: number;
    like_ratio: number;
  };
  by_agent: Array<{
    agent_type: string;
    like: number;
    dislike: number;
    total: number;
  }>;
  recent_days: number;
}

interface FeedbackListResponse {
  items: MessageFeedbackItem[];
  total: number;
  limit: number;
  offset: number;
}

// API helpers
async function fetchApi<T>(url: string): Promise<T> {
  const token = localStorage.getItem("admin_token");
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

async function postApi<T>(
  url: string,
  body: Record<string, unknown>
): Promise<T> {
  const token = localStorage.getItem("admin_token");
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export function MessageFeedbackPage() {
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [items, setItems] = useState<MessageFeedbackItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [feedbackType, setFeedbackType] = useState<string>("");
  const [agentType, setAgentType] = useState<string>("");
  const [days, setDays] = useState<number>(30);
  const [page, setPage] = useState(0);
  const limit = 20;

  // Selected item for detail view
  const [selectedItem, setSelectedItem] = useState<MessageFeedbackItem | null>(
    null
  );
  const [soulmateImageUrl, setSoulmateImageUrl] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const data = await fetchApi<FeedbackStats>(
        `/v1/admin/message-feedback/stats?days=${days}`
      );
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, [days]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (feedbackType) params.set("feedback_type", feedbackType);
      if (agentType) params.set("agent_type", agentType);
      if (days) params.set("days", String(days));
      params.set("limit", String(limit));
      params.set("offset", String(page * limit));

      const data = await fetchApi<FeedbackListResponse>(
        `/v1/admin/message-feedback?${params}`
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [feedbackType, agentType, days, page]);

  useEffect(() => {
    fetchStats();
    fetchList();
  }, [fetchStats, fetchList]);

  // Fetch soulmate image URL when a soulmate/soul_echo feedback is selected
  useEffect(() => {
    if (!selectedItem) {
      setSoulmateImageUrl(null);
      return;
    }

    // Only fetch for soulmate or soul_echo agent types
    const isSoulmateAgent =
      selectedItem.agent_type === "soulmate" ||
      selectedItem.agent_type === "soul_echo";
    if (!isSoulmateAgent) {
      setSoulmateImageUrl(null);
      return;
    }

    // Get imageUrl from context_data (stored without query params)
    const imageUrl = selectedItem.context_data?.imageUrl as string | undefined;
    if (!imageUrl) {
      setSoulmateImageUrl(null);
      return;
    }

    // Check if it needs presigning (internal MinIO URL)
    // MinIO URLs typically contain our domain, CDN URLs are public
    const needsPresign =
      imageUrl.includes("minio") ||
      imageUrl.includes("oss") ||
      imageUrl.includes("sealos");

    if (!needsPresign) {
      // Public CDN URL, use directly
      setSoulmateImageUrl(imageUrl);
      return;
    }

    // Extract path from URL for presigning (bucket/object/path.png)
    // URL format: https://host/bucket/object/path.png
    let objectPath: string | undefined;
    try {
      const url = new URL(imageUrl);
      // pathname is like /bucket/path/to/image.png, remove leading slash
      objectPath = url.pathname.replace(/^\//, "");
    } catch {
      console.error("Invalid image URL:", imageUrl);
      setSoulmateImageUrl(null);
      return;
    }

    if (!objectPath) {
      setSoulmateImageUrl(null);
      return;
    }

    setLoadingImage(true);
    // Backend will split objectPath into bucket + object_name
    postApi<{ image_url: string }>("/v1/admin/message-feedback/presign-image", {
      object_name: objectPath,
    })
      .then((data) => {
        setSoulmateImageUrl(data.image_url);
      })
      .catch((err) => {
        console.error("Failed to presign image:", err);
        setSoulmateImageUrl(null);
      })
      .finally(() => {
        setLoadingImage(false);
      });
  }, [selectedItem]);

  const handleRefresh = () => {
    fetchStats();
    fetchList();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncate = (text: string | null, maxLen: number = 100) => {
    if (!text) return "-";
    return text.length > maxLen ? text.slice(0, maxLen) + "..." : text;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">消息反馈</h1>
            <p className="text-sm text-gray-500 mt-1">
              用户对 AI 回复的点赞/点踩反馈数据
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          刷新
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <ThumbsUp className="w-4 h-4 text-green-500" />
              点赞
            </div>
            <div className="text-2xl font-bold text-green-600">
              {stats.overall.like}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
              <ThumbsDown className="w-4 h-4 text-red-500" />
              点踩
            </div>
            <div className="text-2xl font-bold text-red-600">
              {stats.overall.dislike}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-gray-500 text-sm mb-2">总计</div>
            <div className="text-2xl font-bold text-gray-900">
              {stats.overall.total}
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-gray-500 text-sm mb-2">点赞率</div>
            <div className="text-2xl font-bold text-blue-600">
              {(stats.overall.like_ratio * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      {/* By Agent Stats */}
      {stats && stats.by_agent.length > 0 && (
        <div className="bg-white rounded-lg border p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">按 Agent 类型统计</h3>
          <div className="flex flex-wrap gap-4">
            {stats.by_agent.map((agent) => (
              <div
                key={agent.agent_type}
                className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg"
              >
                <span className="font-medium text-gray-700">
                  {agent.agent_type}
                </span>
                <span className="text-green-600 flex items-center gap-1">
                  <ThumbsUp className="w-3 h-3" /> {agent.like}
                </span>
                <span className="text-red-600 flex items-center gap-1">
                  <ThumbsDown className="w-3 h-3" /> {agent.dislike}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex items-center gap-2 text-gray-700 mb-3">
          <Filter className="w-4 h-4" />
          <span className="font-medium">筛选</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <select
            value={feedbackType}
            onChange={(e) => {
              setFeedbackType(e.target.value);
              setPage(0);
            }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">全部类型</option>
            <option value="like">点赞</option>
            <option value="dislike">点踩</option>
          </select>
          <select
            value={agentType}
            onChange={(e) => {
              setAgentType(e.target.value);
              setPage(0);
            }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">全部 Agent</option>
            <option value="dora">dora</option>
            <option value="soulmate">soulmate</option>
            <option value="soul_echo">soul_echo</option>
          </select>
          <select
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPage(0);
            }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value={7}>最近 7 天</option>
            <option value={30}>最近 30 天</option>
            <option value={90}>最近 90 天</option>
            <option value={365}>最近 1 年</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                类型
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                用户
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                用户消息
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                AI 回复
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                Agent
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                时间
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  加载中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  暂无数据
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                >
                  <td className="px-4 py-3">
                    {item.feedback_type === "like" ? (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <ThumbsUp className="w-4 h-4" /> 赞
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <ThumbsDown className="w-4 h-4" /> 踩
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                    {item.user_id.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {truncate(item.user_message, 50)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {truncate(item.assistant_message, 50)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.agent_type || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(item.created_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {total > limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <span className="text-sm text-gray-500">
              共 {total} 条，第 {page + 1} / {Math.ceil(total / limit)} 页
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-auto m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-medium text-lg">反馈详情</h3>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Type & Time */}
              <div className="flex items-center gap-4">
                {selectedItem.feedback_type === "like" ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full">
                    <ThumbsUp className="w-4 h-4" /> 点赞
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full">
                    <ThumbsDown className="w-4 h-4" /> 点踩
                  </span>
                )}
                <span className="text-sm text-gray-500">
                  {formatDate(selectedItem.created_at)}
                </span>
              </div>

              {/* User Message */}
              <div>
                <div className="text-sm font-medium text-gray-600 mb-1">
                  用户消息
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-sm">
                  {selectedItem.user_message || "(无)"}
                </div>
              </div>

              {/* Assistant Message */}
              <div>
                <div className="text-sm font-medium text-gray-600 mb-1">
                  AI 回复
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap">
                  {selectedItem.assistant_message || "(无)"}
                </div>
              </div>

              {/* Note */}
              {selectedItem.feedback_note && (
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    用户备注
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg text-sm">
                    {selectedItem.feedback_note}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">User ID:</span>{" "}
                  <span className="font-mono">{selectedItem.user_id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Agent:</span>{" "}
                  {selectedItem.agent_type || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Topic ID:</span>{" "}
                  <span className="font-mono text-xs">
                    {selectedItem.topic_id}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Run ID:</span>{" "}
                  <span className="font-mono text-xs">
                    {selectedItem.run_id}
                  </span>
                </div>
              </div>

              {/* Soulmate Image */}
              {(selectedItem.agent_type === "soulmate" ||
                selectedItem.agent_type === "soul_echo") && (
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    生成图片
                  </div>
                  {loadingImage ? (
                    <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                      加载图片中...
                    </div>
                  ) : soulmateImageUrl ? (
                    <div className="mb-3">
                      <img
                        src={soulmateImageUrl}
                        alt="Generated soulmate"
                        className="max-w-xs rounded-lg border"
                      />
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
                      无法加载图片
                    </div>
                  )}
                </div>
              )}

              {/* Context Data */}
              {selectedItem.context_data &&
                Object.keys(selectedItem.context_data).length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">
                      上下文数据
                    </div>
                    {/* Show title if available */}
                    {selectedItem.context_data.title ? (
                      <div className="mb-2 text-sm">
                        <span className="text-gray-500">标题:</span>{" "}
                        <span className="font-medium">
                          {String(selectedItem.context_data.title)}
                        </span>
                      </div>
                    ) : null}
                    {/* Show mode and generation date */}
                    <div className="flex gap-4 mb-2 text-sm">
                      {selectedItem.context_data.mode ? (
                        <div>
                          <span className="text-gray-500">模式:</span>{" "}
                          {String(selectedItem.context_data.mode)}
                        </div>
                      ) : null}
                      {selectedItem.context_data.generationDate ? (
                        <div>
                          <span className="text-gray-500">生成日期:</span>{" "}
                          {String(selectedItem.context_data.generationDate)}
                        </div>
                      ) : null}
                    </div>
                    {/* Show raw JSON for other data */}
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer">
                        查看原始 JSON
                      </summary>
                      <pre className="p-3 bg-gray-100 rounded-lg text-xs overflow-auto mt-2">
                        {JSON.stringify(selectedItem.context_data, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}

              {/* Device Info */}
              {selectedItem.device_info &&
                Object.keys(selectedItem.device_info).length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-600 mb-1">
                      设备信息
                    </div>
                    <pre className="p-3 bg-gray-100 rounded-lg text-xs overflow-auto">
                      {JSON.stringify(selectedItem.device_info, null, 2)}
                    </pre>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageFeedbackPage;
